// Part C — public, no-login patient consent/signing page. Follows
// ResetPassword.js's no-auth token-page convention exactly: reads `token`
// from useSearchParams, no ProtectedRoute wrapper, no UserContext needed.
//
// Two-step flow:
//   1. DOB entry/verification (defense-in-depth gate — holding the link
//      alone is not enough) -> short-lived `session` token.
//   2. Read-only requisition review + SignaturePad + submit, gated on
//      that session token.
//
// Idempotency: submit uses the SAME idempotency-key discipline as Order
// a Test's own submission (see OrderTest.js's generateIdempotencyKey) —
// generated once when the review/sign step is reached, sent with every
// submit attempt (including a client-side retry after a network error),
// so the patient can never end up unsure whether their signature went
// through.
import React, { useEffect, useRef, useState } from "react";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Divider, Chip,
  Checkbox, FormControlLabel,
} from "@mui/material";
import { CheckCircleOutline, LockOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import SignaturePad from "./SignaturePad";
import ThemeToggle from "./ThemeToggle";
import { generateIdempotencyKey } from "./OrderTest";
import { PROOVE_PGX_SCHEMA_KEY } from "./orderTestDerivation";
import DynamicRequisitionForm from "./DynamicRequisitionForm";
import { extractErrorDetail } from "./validators";
// 2026-10-13 fix — this page rendered raw internal placeholder codes
// (e.g. "tox_placeholder_1" for test_ids, and the raw schema_key when a
// requisition had no `family`) directly to the PATIENT on the document
// they are legally signing. MyPatients.js already solved exactly this
// display problem for the clinician-facing order history view with its
// own TEST_ID_DISPLAY_NAMES map — reused here verbatim (now extracted to
// src/testDisplayNames.js so both files import the SAME map instead of
// drifting two hand-typed copies) rather than inventing a second one.
import { displayNameForTestId, displayNameForFamily } from "../testDisplayNames";

// 2026-10-13 fix — Biobank applicability by schema_key, NOT "is this a
// Proove order". Confirmed against labariumaireport/backend's own
// ORDER_CONSENTS_TEXT (order_requisition_schema.py) and requisition_pdf.py,
// the authoritative source for what consent language a signed requisition
// actually carries per schema_key:
//   - proove_pgx_v1 — Biobank language is hardcoded in requisition_pdf.py's
//     _PAGE2_HTML_PROOVE_CONSENTS block (the fallback used whenever
//     ORDER_CONSENTS_TEXT has no override for a schema_key).
//   - toxicology_v1 — ALSO has real Biobank language, verbatim from the
//     paper tox form, in ORDER_CONSENTS_TEXT[TOXICOLOGY_SCHEMA_KEY]
//     ["legal_paragraphs"][1] ("...I consent to have my specimen stored
//     for research purposes... ("Biobank")..."). This directly
//     contradicts an earlier assumption that only Proove had a Biobank
//     concept — verified by reading the dict itself, not taken on faith.
//   - infectious_disease_molecular_v1 — ORDER_CONSENTS_TEXT has NO Biobank
//     (or "Provider Parties") language at all for this key; its one
//     legal_paragraphs entry is a plain test/billing consent.
// PROOVE_PGX_SCHEMA_KEY is imported (not re-literaled) from
// orderTestDerivation.js, the same module OrderTest.js itself imports it
// from, so Proove-family detection never drifts between the two
// consent-collecting surfaces. "toxicology_v1" has no equivalent exported
// frontend constant yet (it's never needed one before this fix) so it's
// named directly here, matching order_requisition_schema.py's own
// TOXICOLOGY_SCHEMA_KEY = "toxicology_v1" literal.
const BIOBANK_ELIGIBLE_SCHEMA_KEYS = new Set([PROOVE_PGX_SCHEMA_KEY, "toxicology_v1"]);

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

function Field({ label, required, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.06em", color: sub,
        textTransform: "uppercase", mb: 1 }}>
        {label}{required && <Box component="span" sx={{ color: BRAND.gold, ml: 0.4 }}>*</Box>}
      </Typography>
      {children}
    </Box>
  );
}

function Shell({ children, isDark, bg, cardBg }) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column" }}>
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.97)",
        borderBottom: "2px solid rgba(192,168,140,0.3)" }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 64,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
            sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
          </Box>
          <ThemeToggle />
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 3, py: 6 }}>
        <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45 }}>
          <Box sx={{ width: { xs: "100%", sm: 520 }, maxWidth: 680, borderRadius: 3, overflow: "hidden",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
            boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
            bgcolor: cardBg }}>
            <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />
            {children}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}

export default function PatientConsent() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const bg = isDark ? "#080d1a" : "#f4f7ff";
  const cardBg = isDark ? "#111827" : "#ffffff";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  // "loading" | "invalid" | "dob" | "review" | "done"
  const [phase, setPhase] = useState("loading");
  const [lookup, setLookup] = useState(null);
  const [dob, setDob] = useState("");
  const [session, setSession] = useState(null);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [idempotencyKey, setIdempotencyKey] = useState(null);
  const sigRef = useRef(null);
  // 2026-10-08 — moved here from OrderTest.js's Step 4 (in-office patient
  // signing removed): this is now the ONLY place a patient gives their
  // own consent, drawn verbatim from the same legal text that used to
  // live on the clinician's Review & Sign step. Patient Consent and
  // Authorization is the actual clinical-service authorization and is
  // REQUIRED; Biobank Participation is a SEPARATE, non-defaulted,
  // optional checkbox — never bundled with the required consent.
  const [patientConsent, setPatientConsent] = useState(false);
  const [biobankConsent, setBiobankConsent] = useState(false);

  const genericInvalidMsg = "This link is no longer valid. Please contact your provider for a new consent link.";

  useEffect(() => {
    if (!orderId || !token) {
      setPhase("invalid");
      setError("This link is missing required information.");
      return;
    }
    axios.get(`${API_BASE}/patient-consent/${orderId}`, { params: { token } })
      .then((res) => {
        setLookup(res.data);
        setPhase("dob");
      })
      .catch((err) => {
        setPhase("invalid");
        setError(extractErrorDetail(err, genericInvalidMsg));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    if (!dob) return setError("Please enter your date of birth.");
    setVerifying(true);
    try {
      const res = await axios.post(`${API_BASE}/patient-consent/${orderId}/verify`, { token, dob });
      setSession(res.data.session);
      const d = await axios.get(`${API_BASE}/patient-consent/${orderId}/details`, {
        params: { session: res.data.session },
      });
      setDetails(d.data);
      setIdempotencyKey(generateIdempotencyKey());
      setPhase("review");
    } catch (err) {
      setError(extractErrorDetail(err, genericInvalidMsg));
      // A locked-out token looks the same (generic) from the server by
      // design — the DOB field just stays available to retry until the
      // server starts returning the generic invalid response for every
      // attempt (indistinguishable from expired/consumed, also by design).
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    setError("");
    if (!patientConsent) return setError("Please consent to the Services before signing.");
    const sigB64 = sigRef.current?.getPngBase64();
    if (!sigB64) return setError("Please sign before submitting.");
    submittingRef.current = true;
    setSubmitting(true);
    const key = idempotencyKey || generateIdempotencyKey();
    if (!idempotencyKey) setIdempotencyKey(key);
    // Defensive even though the checkbox's only setter lives inside the
    // `biobankApplicable &&` block below (so biobankConsent can't
    // actually be set true when hidden today) — never let a hidden-box
    // order submit `biobank_consent: true` for a schema_key whose real
    // signed consent has no Biobank clause at all.
    //
    // 2026-10-13 fix — must use the SAME eligibility check as
    // `biobankApplicable` above (including the schema_key == null
    // legacy-order case), not just BIOBANK_ELIGIBLE_SCHEMA_KEYS.has(...).
    // Before this fix, a legacy order (schema_key: null) rendered the
    // checkbox as eligible (biobankApplicable above already included the
    // null case) but this separate, un-synced check here still evaluated
    // to false for that same order, silently discarding whatever the
    // patient actually chose and submitting `biobank_consent: false`
    // regardless — storing a false "declined" that was never genuinely
    // asked or answered, the exact bug class this whole feature exists
    // to prevent.
    const biobankApplicableAtSubmit = (details?.requisitions || [])
      .some((r) => r.schema_key == null || BIOBANK_ELIGIBLE_SCHEMA_KEYS.has(r.schema_key));
    try {
      await axios.post(`${API_BASE}/patient-consent/${orderId}/submit`, {
        session, patient_signature_png_base64: sigB64, idempotency_key: key,
        patient_consent: patientConsent,
        biobank_consent: biobankApplicableAtSubmit ? biobankConsent : false,
      });
      setPhase("done");
    } catch (err) {
      // Network failure / ambiguous timeout — the SAME idempotency_key is
      // reused on a user-initiated retry (the key is only regenerated on
      // a genuinely new attempt, never on retry), so re-clicking Submit
      // below safely replays rather than risking a second signature.
      setError(extractErrorDetail(err,
        "We could not confirm your signature was received. Please check your connection and press Submit again — it is safe to retry."));
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  if (phase === "loading") {
    return (
      <Shell isDark={isDark} bg={bg} cardBg={cardBg}>
        <Box sx={{ px: 4, py: 6, textAlign: "center" }}><CircularProgress /></Box>
      </Shell>
    );
  }

  if (phase === "invalid") {
    return (
      <Shell isDark={isDark} bg={bg} cardBg={cardBg}>
        <Box sx={{ px: 4, py: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error || genericInvalidMsg}</Alert>
        </Box>
      </Shell>
    );
  }

  if (phase === "done") {
    return (
      <Shell isDark={isDark} bg={bg} cardBg={cardBg}>
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center" }}>
          <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>Signed — thank you</Typography>
        </Box>
        <Box sx={{ px: 4, py: 3.5 }}>
          <Typography sx={{ fontSize: 14.5, color: textSub, lineHeight: 1.8, textAlign: "center" }}>
            Your signature has been recorded for this order. You may now close this page.
          </Typography>
        </Box>
      </Shell>
    );
  }

  if (phase === "dob") {
    return (
      <Shell isDark={isDark} bg={bg} cardBg={cardBg}>
        <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3, pb: 3.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, mb: 1 }}>
            PATIENT CONSENT
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: "#fff" }}>
            Confirm it's you, {lookup?.first_name || "there"}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)", mt: 0.5 }}>
            Record on file: {lookup?.first_name} {lookup?.last_name} — DOB {lookup?.dob_hint}
          </Typography>
        </Box>
        <Box sx={{ px: 4, py: 4 }} component="form" onSubmit={handleVerify} noValidate>
          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}
          <Field label="Your date of birth" required isDark={isDark}>
            <TextField hiddenLabel fullWidth required type="date" value={dob}
              onChange={(e) => setDob(e.target.value)} />
          </Field>
          <Typography sx={{ fontSize: 12.5, color: textSub, mb: 2.5, display: "flex", alignItems: "center", gap: 0.5 }}>
            <LockOutlined sx={{ fontSize: 14 }} /> We verify this to protect your record — it must match what your provider has on file.
          </Typography>
          <Button type="submit" fullWidth variant="contained" disabled={verifying}
            sx={{ py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17, borderRadius: "10px",
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)` }}>
            {verifying ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Continue"}
          </Button>
        </Box>
      </Shell>
    );
  }

  // phase === "review"
  // Whether ANY requisition in this order actually carries Biobank
  // language in its real, schema-specific signed consent text (see
  // BIOBANK_ELIGIBLE_SCHEMA_KEYS above) — a multi-family order (e.g.
  // Proove + Infectious Disease bundled together) still shows the box,
  // since the Proove requisition's own consent genuinely has it, even
  // though the ID requisition's does not.
  //
  // 2026-10-13 fix — a requisition with schema_key == null/undefined is
  // ALSO treated as eligible here, to match main.py's
  // _biobank_applicable_for_order (`r.get("schema_key") is None`):
  // `_requisitions_for_order_doc`'s legacy-compat shim synthesizes
  // schema_key=None (not "") for pre-Part-B order docs, and every such
  // order predates the Toxicology/ID families — it was always implicitly
  // a Proove order. Deliberately `== null` (covers null/undefined only,
  // via JS's loose-equality null/undefined special case), NOT a falsy
  // check — an empty-string schema_key is a materially different case
  // (not what the legacy shim produces) and must NOT be swept in here,
  // or this would just trade one frontend/backend divergence for a new
  // one. Without this, the backend would treat a legacy order as
  // Biobank-applicable (and could store a client-sent `false` as a
  // genuine "declined" answer) while this checkbox stayed hidden on
  // screen — reproducing the exact "storing False as a real decision
  // when nothing was asked" bug this feature was built to prevent.
  const biobankApplicable = (details?.requisitions || [])
    .some((r) => r.schema_key == null || BIOBANK_ELIGIBLE_SCHEMA_KEYS.has(r.schema_key));

  return (
    <Shell isDark={isDark} bg={bg} cardBg={cardBg}>
      <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3, pb: 3.5 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, mb: 1 }}>
          REVIEW &amp; SIGN
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>Your lab order</Typography>
      </Box>
      <Box sx={{ px: 4, py: 4 }}>
        {error && <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

        {/* 2026-10-10 fix — this used to be a raw Object.entries(submitted_data)
            dump: every field (including ones meant to be grouped, e.g.
            billing/insurance under "Patient & Billing") as an unlabeled
            `field_id: value` row in whatever order Object.entries happened
            to iterate, plus literal "[object Object]" text for any field
            whose value is itself an array of objects (coded ICD selections)
            or a plain object (PSS-10/PHQ-2 questionnaire answers). The
            founder's own ask was to show this "uneditable, that web form
            like thing" — i.e. the ACTUAL requisition form, read-only, not a
            debug dump. patient_consent_details now fetches each
            requisition's own schema server-side (same internal call
            get_clinician_order_requisition_schema uses, just without that
            route's clinician gate — this page is already session-gated) and
            returns it alongside submitted_data, so this renders through the
            EXACT SAME DynamicRequisitionForm the clinician's own Order a
            Test / My Patients screens use — same section groupings, same
            field labels, same option labels, same coded/structured-field
            rendering — in readOnly mode, instead of a second hand-built
            formatter that could drift out of sync with it.

            Each requisition renders from ITS OWN r.schema — never a shared
            schema across requisitions — so a multi-family order (e.g.
            Proove PGx + Toxicology bundled together) shows each
            requisition's genuinely different fields/sections side by side,
            not both converging on whichever schema loaded first. */}
        {(details?.requisitions || []).map((r, i) => (
          <Box key={i} sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Chip size="small" label={displayNameForFamily(r.family, r.schema_key)} sx={{ bgcolor: BRAND.navy, color: "#fff", fontWeight: 700 }} />
              <Typography sx={{ fontSize: 13, color: textSub }}>
                {(r.test_ids || []).map((tid) => displayNameForTestId(tid)).join(", ")}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,53,122,0.03)", borderRadius: 2, p: 2 }}>
              {Array.isArray(r.schema) ? (
                <DynamicRequisitionForm
                  schema={r.schema}
                  data={r.submitted_data || {}}
                  onChange={() => {}}
                  isDark={isDark}
                  readOnly
                />
              ) : (
                <>
                  {/* 2026-10-10 — graceful per-requisition degradation: the
                      server-side schema fetch for THIS ONE schema_key
                      failed (internal API hiccup), while every other
                      requisition in the order loaded fine. Falls back to
                      the old flat dump for just this one, with a visible
                      note, rather than blocking the patient from
                      reviewing/signing the rest of the order. */}
                  <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 1.5, fontSize: 12.5 }}>
                    We couldn't load this section's full form layout — showing the submitted values below instead.
                  </Alert>
                  {Object.entries(r.submitted_data || {}).map(([k, v]) => (
                    v !== null && v !== "" && (
                      <Box key={k} sx={{ display: "flex", justifyContent: "space-between", py: 0.4, fontSize: 13.5 }}>
                        <Typography sx={{ color: textSub, mr: 2 }}>{k.replace(/_/g, " ")}</Typography>
                        <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
                          {Array.isArray(v) ? v.join(", ") : (typeof v === "object" ? JSON.stringify(v) : String(v))}
                        </Typography>
                      </Box>
                    )
                  ))}
                </>
              )}
            </Box>
            <Divider sx={{ mt: 2.5 }} />
          </Box>
        ))}

        <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.06em", color: textSub,
          textTransform: "uppercase", mb: 1, mt: 1 }}>
          Patient Consents and Authorizations
        </Typography>
        <FormControlLabel
          sx={{ alignItems: "flex-start", mt: 0.5 }}
          control={<Checkbox checked={patientConsent} onChange={(e) => setPatientConsent(e.target.checked)}
            sx={{ color: BRAND.navy, mt: -0.5, "&.Mui-checked": { color: BRAND.navy } }} />}
          label={<Typography sx={{ fontSize: 13, color: textSub, lineHeight: 1.6 }}>
            I voluntarily consent to having these Services performed and the results made available to
            my ordering provider and healthcare professionals. I authorize the use, access, and
            disclosure of my protected health information as needed to perform, report, and bill for
            the Services, as further detailed in the Consents and Authorizations I am signing for this
            order{(details?.requisitions || []).length > 1 ? "'s requisitions" : "'s requisition"}.
          </Typography>}
        />

        {/* 2026-10-13 fix — previously rendered unconditionally for every
            order. "Provider Parties" (removed above) is Proove/Toxicology-
            specific defined-term language that never appears in the
            Infectious Disease/Molecular form's own legal text at all — see
            BIOBANK_ELIGIBLE_SCHEMA_KEYS' header comment for the verified
            per-schema_key source. Deliberately a separate, non-defaulted
            box when shown — never bundled with the required consent
            above. */}
        {biobankApplicable && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "rgba(201,169,110,0.08)", border: "1px solid rgba(201,169,110,0.25)" }}>
            <FormControlLabel
              sx={{ alignItems: "flex-start" }}
              control={<Checkbox checked={biobankConsent} onChange={(e) => setBiobankConsent(e.target.checked)}
                sx={{ color: BRAND.gold, mt: -0.5, "&.Mui-checked": { color: BRAND.gold } }} />}
              label={<Typography sx={{ fontSize: 13, color: textSub, lineHeight: 1.6 }}>
                <strong>Optional — Biobank Participation.</strong> I separately and voluntarily consent to having my
                specimen stored in and used by the Labarium Biobank for future research. This is entirely
                independent of my clinical Services — declining has no effect on my care, and this box is
                never required to continue.
              </Typography>}
            />
          </Box>
        )}

        <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.06em", color: textSub,
          textTransform: "uppercase", mb: 1, mt: 3 }}>
          Your signature<Box component="span" sx={{ color: BRAND.gold, ml: 0.4 }}>*</Box>
        </Typography>
        <SignaturePad ref={sigRef} isDark={isDark} height={120} />

        <Button fullWidth variant="contained" disabled={submitting || !patientConsent} onClick={handleSubmit}
          sx={{ mt: 3, py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17, borderRadius: "10px",
            background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)` }}>
          {submitting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Sign & Submit"}
        </Button>
      </Box>
    </Shell>
  );
}
